import React, { useState, useEffect, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { urls } from './config.dev';
import { useNavigate } from 'react-router-dom';
import { faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Topbar from './Topbar';
import './Triage.css';


function Triage() {
    const [newPatientDetails, setNewPatientDetails] = useState({
        firstName: '',
        lastName: '',
        contactId: '',
        phoneNumber: '',
        years: '',
        months: '',
        weeks: '',
        sex: '',
        religion: '',
        dob: '',
        address: '',
    });

    const [bloodPressure, setBloodPressure] = useState('');
    const [employeeName, setEmployeeName] = useState('');
    const [temperature, setTemperature] = useState('');
    const [spo2, setSpo2] = useState('');
    const [bodyWeight, setBodyWeight] = useState('');
    const [labTestsAvailable, setLabTestsAvailable] = useState([]);
    const [selectedLabTests, setSelectedLabTests] = useState([]);
    const [radiologyTestsAvailable, setRadiologyTestsAvailable] = useState([]);
    const [selectedRadiologyTests, setSelectedRadiologyTests] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [savePatientClicked, setSavePatientClicked] = useState(false);
    const [showAddPatientPrompt, setShowAddPatientPrompt] = useState(false);
    const [message, setMessage] = useState('');
    const navigate = useNavigate();
    const [readOnlyPatientDetails, setReadOnlyPatientDetails] = useState(null);
    const [showLabTestPrompt, setShowLabTestPrompt] = useState(false);
    const [showRadiologyTestPrompt, setShowRadiologyTestPrompt] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [signsAndSymptoms, setSignsAndSymptoms] = useState('');
    const [pulseRate, setPulseRate] = useState('');
    const [respiratoryRate, setRespiratoryRate] = useState('');
    const [height, setHeight] = useState('');
    const [bmi, setBmi] = useState('');
    const [bmiWarning, setBmiWarning] = useState('');
    const [successModal, setSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [patientAction, setPatientAction] = useState('');

    const [selectionState, setSelectionState] = useState({
        doctor: false,
        lab: false,
        radiology: false,
        familyPlanning: false,
    });

    const [attentionLevel, setAttentionLevel] = useState('');
    const [patientNature, setPatientNature] = useState('');

    const [pulseRateStatus, setPulseRateStatus] = useState({ status: '', level: '' });
    const [bloodPressureStatus, setBloodPressureStatus] = useState({ status: '', level: '' });
    const [temperatureStatus, setTemperatureStatus] = useState('');
    const [spo2Status, setSpo2Status] = useState('');
    const [respiratoryRateStatus, setRespiratoryRateStatus] = useState('');

    const debounceTimer = useRef(null);
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');

    // Fetch employee name and perform security check
    useEffect(() => {
        const fetchEmployeeName = async () => {
            try {
                const securityResponse = await fetch(urls.security, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ token: urlToken }),
                });

                if (securityResponse.ok) {
                    const securityData = await securityResponse.json();
                    if (securityData.message === 'Session valid') {
                        setEmployeeName(securityData.employee_name);
                        localStorage.setItem('token', securityData.clinic_session_token);
                        fetchAvailableLabTests();
                        fetchAvailableRadiologyTests();
                    } else if (securityData.error === 'Session expired') {
                        navigate(`/dashboard?token=${securityData.clinic_session_token}`);
                    } else {
                        navigate('/login');
                    }
                } else {
                    throw new Error('Failed to perform security check');
                }
            } catch (error) {
                console.error('Error performing security check:', error);
                navigate('/login');
            }
        };

        fetchEmployeeName();
    }, [navigate, urlToken]);

    // Fetch available lab tests
    const fetchAvailableLabTests = async () => {
        try {
            const response = await fetch(urls.testsavailable, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: urlToken }),
            });

            if (!response.ok) throw new Error('Failed to fetch available lab tests');
            const data = await response.json();
            setLabTestsAvailable(data);
        } catch (error) {
            console.error('Error fetching available lab tests:', error);
        }
    };

    // Fetch available radiology tests
    const fetchAvailableRadiologyTests = async () => {
        try {
            const response = await fetch(urls.radiologytestsavailable, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: urlToken }),
            });

            if (!response.ok) throw new Error('Failed to fetch available radiology exams');
            const data = await response.json();
            setRadiologyTestsAvailable(data);
        } catch (error) {
            console.error('Error fetching available radiology exams:', error);
        }
    };

    // Handle name suggestions with debounce
    const handleSuggestName = async (name) => {
        // Check if name contains invalid characters
        const invalidChars = /[^a-zA-Z\s'-]/;
        if (invalidChars.test(name)) {
            toast.error(`Please ${employeeName}, punctuation marks cannot be used in names. Kindly remove the panctuation mark and  hit the space bar in case you want to cancel suggestions.`, {
                autoClose: 15000,
            });
            setSuggestions([]);
            return;
        }

        // Clear previous timer
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        // Set new timer
        debounceTimer.current = setTimeout(async () => {
            try {
                const queryString = `?name=${encodeURIComponent(name)}&token=${urlToken}`;
                const response = await fetch(`${urls.suggest}${queryString}`);
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.message && data.message === 'No patient records found for the specified clinic') {
                        setSuggestions([]);
                        setMessage('No patient records found');
                    } else if (data.error === 'Name and token parameters are required') {
                        setSuggestions([]);
                        setMessage('No patient records found');
                    } else {
                        setSuggestions(data);
                        setMessage('');
                    }
                } else {
                    const errorData = await response.json();
                    if (errorData.error !== "Name parameter is required") {
                        throw new Error('Failed to suggest names');
                    }
                }
            } catch (error) {
                console.error('Error suggesting names:', error.message);
                setSuggestions([]);
                setMessage('No patient records found');
            }
        }, 300); // 300ms debounce delay
    };

    // Calculate BMI
    const calculateBMI = (weight, height) => {
        if (weight && height) {
            const heightInMeters = height / 100;
            return (weight / (heightInMeters * heightInMeters)).toFixed(2);
        }
        return '';
    };

    // Get BMI warning
    const getBMIWarning = (bmi) => {
        if (!bmi) return '';
        const bmiValue = parseFloat(bmi);
        if (bmiValue < 18.5) return 'Warning: Underweight';
        if (bmiValue >= 18.5 && bmiValue <= 24.9) return 'Normal: Healthy weight';
        if (bmiValue >= 25 && bmiValue <= 29.9) return 'Warning: Overweight';
        if (bmiValue >= 30) return 'Warning: Obese';
        return '';
    };

    // Update BMI when weight or height changes
    useEffect(() => {
        const calculatedBmi = calculateBMI(bodyWeight, height);
        setBmi(calculatedBmi);
        setBmiWarning(getBMIWarning(calculatedBmi));
    }, [bodyWeight, height]);

    // Classify pulse rate
    const classifyPulseRate = (pulseRate) => {
        const pulse = parseInt(pulseRate, 10);
        if (isNaN(pulse)) return { status: '', level: '' };
        if (pulse < 60) return { status: 'Warning: Low Pulse Rate', level: 'low' };
        if (pulse > 100) return { status: 'Warning: High Pulse Rate', level: 'high' };
        return { status: 'Pulse Rate Normal', level: 'normal' };
    };

    // Classify blood pressure
    const classifyBloodPressure = (bloodPressure) => {
        if (!bloodPressure.includes('/')) return { status: '', level: '' };
        const [systolic, diastolic] = bloodPressure.split('/').map(Number);
        if (isNaN(systolic) || isNaN(diastolic)) return { status: '', level: '' };
        if (systolic < 90 || diastolic < 60) return { status: 'Warning: Low Blood Pressure', level: 'low' };
        if (systolic > 140 || diastolic > 90) return { status: 'Warning: High Blood Pressure', level: 'high' };
        return { status: 'Blood Pressure Normal', level: 'normal' };
    };

    // Classify temperature
    const classifyTemperature = (temperature) => {
        const temp = parseFloat(temperature);
        if (isNaN(temp)) return '';
        if (temp < 35) return 'Warning: Very Low Temperature';
        if (temp >= 35 && temp < 36.5) return 'Warning: Low Temperature';
        if (temp >= 36.5 && temp <= 37.5) return 'Temperature Normal';
        if (temp > 37.5 && temp <= 38.5) return 'Warning: High Temperature';
        return 'Warning: Very High Temperature';
    };

    // Classify respiratory rate
    const classifyRespiratoryRate = (rate) => {
        const respiratoryRate = parseInt(rate, 10);
        if (isNaN(respiratoryRate)) return '';
        if (respiratoryRate < 12) return 'Warning: Low Respiratory Rate';
        if (respiratoryRate > 20) return 'Warning: High Respiratory Rate';
        return 'Respiratory Rate Normal';
    };

    // Classify SPO2
    const classifySPO2 = (spo2) => {
        const spo2Value = parseFloat(spo2);
        if (isNaN(spo2Value)) return '';
        if (spo2Value < 90) return 'Warning: Low SPO2';
        return 'SPO2 Normal';
    };

    // Update classifications when vital signs change
    useEffect(() => {
        setPulseRateStatus(classifyPulseRate(pulseRate));
    }, [pulseRate]);

    useEffect(() => {
        setBloodPressureStatus(classifyBloodPressure(bloodPressure));
    }, [bloodPressure]);

    useEffect(() => {
        setTemperatureStatus(classifyTemperature(temperature));
    }, [temperature]);

    useEffect(() => {
        setRespiratoryRateStatus(classifyRespiratoryRate(respiratoryRate));
    }, [respiratoryRate]);

    useEffect(() => {
        setSpo2Status(classifySPO2(spo2));
    }, [spo2]);

    // Handle cancel action
    const handleCancel = () => {
        setNewPatientDetails({
            firstName: '',
            lastName: '',
            contactId: '',
            phoneNumber: '',
            years: '',
            months: '',
            weeks: '',
            sex: '',
            religion: '',
            dob: '',
            address: ''
        });
        setShowAddPatientPrompt(false);
        setSuggestions([]);
    };

    // Add new contact/patient
    const addContact = async () => {
        if (savePatientClicked) return;
        setSavePatientClicked(true);

        if (
            !newPatientDetails.firstName ||
            !newPatientDetails.lastName ||
            !newPatientDetails.phoneNumber ||
            (!newPatientDetails.years && !newPatientDetails.months && !newPatientDetails.weeks) ||
            !newPatientDetails.sex ||
            !newPatientDetails.religion ||
            !newPatientDetails.dob ||
            !newPatientDetails.address
        ) {
            toast.error('Please fill in all required fields including address and at least one age value.');
            setSavePatientClicked(false);
            return;
        }

        try {
            const response = await fetch(urls.addcontact5, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...newPatientDetails,
                    token: urlToken,
                    ageDetails: {
                        years: newPatientDetails.years || 0,
                        months: newPatientDetails.months || 0,
                        weeks: newPatientDetails.weeks || 0,
                    },
                }),
            });

            const data = await response.json();

            if (response.ok) {
                updateReadOnlyPatientDetails(data.patientDetails);
                toast.success(data.message);
                setSavePatientClicked(true);
                setShowAddPatientPrompt(false);
            } else {
                throw new Error(data.message || 'Failed to add new patient');
            }
        } catch (error) {
            console.error('Error adding new patient:', error);
            toast.error(error.message || 'Oops! Something went wrong.');
            setSavePatientClicked(false);
        }
    };

    // Update read-only patient details
    const updateReadOnlyPatientDetails = (newDetails) => {
        setReadOnlyPatientDetails(newDetails);
    };

    // Handle selecting a suggested patient
    const handleSelectSuggestedPatient = (patient) => {
        setNewPatientDetails({
            firstName: patient.first_name,
            lastName: patient.last_name,
            contactId: patient.contact_id,
            phoneNumber: patient.phone_number,
            years: patient.age,
            months: patient.age_months,
            weeks: patient.age_weeks,
            sex: patient.sex,
            religion: patient.religion,
            dob: patient.dob,
        });

        setReadOnlyPatientDetails({
            firstName: patient.first_name,
            lastName: patient.last_name,
            contactId: patient.contact_id,
            phoneNumber: patient.phone_number,
            years: patient.age,
            months: patient.age_months,
            weeks: patient.age_weeks,
            sex: patient.sex,
            religion: patient.religion,
            dob: patient.dob,
        });

        setSuggestions([]);
        setShowAddPatientPrompt(false);
    };

    // Format date
    const formatDate = (date) => {
        return date ? new Date(date).toISOString().split('T')[0] : '';
    };

    // Toggle options (doctor, lab, radiology, family planning)
    const handleOptionToggle = (option) => {
        switch (option) {
            case 'doctor':
                setSelectionState(prev => ({ ...prev, doctor: !prev.doctor }));
                break;
            case 'lab':
                setSelectionState(prev => {
                    const wasSelected = prev.lab;
                    if (wasSelected) {
                        setSelectedLabTests([]);
                        setShowLabTestPrompt(false);
                    } else {
                        setShowLabTestPrompt(true);
                    }
                    return { ...prev, lab: !prev.lab };
                });
                break;
            case 'radiology':
                setSelectionState(prev => {
                    const wasSelected = prev.radiology;
                    if (wasSelected) {
                        setSelectedRadiologyTests([]);
                        setShowRadiologyTestPrompt(false);
                    } else {
                        setShowRadiologyTestPrompt(true);
                    }
                    return { ...prev, radiology: !prev.radiology };
                });
                break;
            case 'familyPlanning':
                setSelectionState(prev => ({ ...prev, familyPlanning: !prev.familyPlanning }));
                break;
            default:
                break;
        }
    };

    // Toggle attention level
    const toggleAttentionLevel = (level) => {
        setAttentionLevel(level);
    };

    // Submit form
    const submitForm = async () => {
        try {
            setSubmitting(true);
            toast.info('Form is being submitted...');

            if (!selectionState.doctor && !selectionState.lab && !selectionState.radiology && !selectionState.familyPlanning) {
                toast.error('Please select at least one option (doctor, lab, radiology, or family planning).', {
                    autoClose: false,
                });
                setSubmitting(false);
                return;
            }

            if (!attentionLevel || !patientNature) {
                toast.error('Attention and Visit Classification are required.', {
                    autoClose: false,
                });
                setSubmitting(false);
                return;
            }

            if (selectionState.doctor && (selectedLabTests.length > 0 || selectedRadiologyTests.length > 0)) {
                toast.error(
                    "If you're sending this patient to the doctor, do not request lab or radiology tests. The doctor will handle these tests.", 
                    { autoClose: false }
                );
                setSubmitting(false);
                return;
            }

            const formData = {
                contactId: readOnlyPatientDetails.contactId,
                bloodPressure,
                temperature,
                spo2,
                bodyWeight,
                height,
                respiratory_rate: respiratoryRate,
                pulse_rate: pulseRate,
                labTests: selectionState.lab ? selectedLabTests : [],
                radiologyExams: selectionState.radiology ? selectedRadiologyTests : [],
                token: urlToken,
                action: selectionState.doctor ? 'Going to the doctor' : '',
                familyPlanning: selectionState.familyPlanning,
                attention: attentionLevel,
                visitClassification: patientNature,
                signsAndSymptoms
            };

            const urlToUse = selectionState.doctor ? urls.gotodoctor : urls.submitwalkinpt2;

            const submitResponse = await fetch(urlToUse, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (submitResponse.ok) {
                const responseData = await submitResponse.json();
                if (responseData.message === 'Data inserted successfully') {
                    toast.success('Patient form submitted successfully');
                    
                    // Set success message based on action
                    if (selectionState.doctor) {
                        setSuccessMessage('Please send the patient to the doctor');
                        setPatientAction('doctor');
                    } else if (selectionState.lab || selectionState.radiology) {
                        setSuccessMessage('Please send the patient to the cashier');
                        setPatientAction('cashier');
                    } else if (selectionState.familyPlanning) {
                        setSuccessMessage('Please send the patient to the family planning department');
                        setPatientAction('familyPlanning');
                    }
                    
                    setSuccessModal(true);
                    
                    // Reset form
                    setReadOnlyPatientDetails(null);
                    setBloodPressure('');
                    setTemperature('');
                    setSpo2('');
                    setBodyWeight('');
                    setHeight('');
                    setRespiratoryRate('');
                    setPulseRate('');
                    setSelectedLabTests([]);
                    setSelectedRadiologyTests([]);
                    setSelectionState({
                        doctor: false,
                        lab: false,
                        radiology: false,
                        familyPlanning: false,
                    });
                    setAttentionLevel('');
                    setPatientNature('');
                    setSignsAndSymptoms('');
                }
            } else {
                throw new Error('Error submitting patient data');
            }
        } catch (error) {
            toast.error('Error submitting form! Make sure you have inserted the patient details.', {
                autoClose: false,
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Display read-only patient fields
    const displayReadOnlyFields = () => {
        if (readOnlyPatientDetails) {
            return (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '15px',
                    marginBottom: '20px',
                    padding: '15px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                    {Object.entries(readOnlyPatientDetails).map(([key, value]) => (
                        <div key={key} style={{ marginBottom: '10px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '5px',
                                fontWeight: '600',
                                color: '#333',
                                textTransform: 'capitalize'
                            }}>
                                {key.replace(/([A-Z])/g, ' $1').trim()}:
                            </label>
                            <input
                                type="text"
                                value={value || ''}
                                readOnly
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    backgroundColor: '#f9f9f9'
                                }}
                            />
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    // Updated styles with a clean black and white theme
    const styles = {
        container: {
           
        
            margin: '20px auto',
            padding: '30px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        },
        header: {
            color: '#333',
            textAlign: 'center',
            marginBottom: '40px',
            fontSize: '28px',
            fontWeight: '600',
            borderBottom: '2px solid #eee',
            paddingBottom: '15px'
        },
        section: {
            marginBottom: '30px',
            padding: '25px',
            backgroundColor: '#fafafa',
            borderRadius: '6px',
            border: '1px solid #eee'
        },
        sectionHeading: {
            color: '#333',
            marginBottom: '20px',
            fontSize: '20px',
            fontWeight: '500'
        },
        button: {
            backgroundColor: '#2c3e50',
            color: 'white',
            padding: '12px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '10px',
            marginBottom: '10px',
            fontSize: '15px',
            fontWeight: '500',
            transition: 'all 0.3s ease',
            ':hover': {
                backgroundColor: '#1a252f',
                transform: 'translateY(-1px)'
            }
        },
        primaryButton: {
            backgroundColor: '#3498db',
            ':hover': {
                backgroundColor: '#2980b9'
            }
        },
        dangerButton: {
            backgroundColor: '#e74c3c',
            ':hover': {
                backgroundColor: '#c0392b'
            }
        },
        successButton: {
            backgroundColor: '#27ae60',
            ':hover': {
                backgroundColor: '#219653'
            }
        },
        input: {
            width: '100%',
            padding: '12px',
            marginBottom: '15px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '15px',
            transition: 'border 0.3s ease',
            ':focus': {
                borderColor: '#3498db',
                outline: 'none',
                boxShadow: '0 0 0 2px rgba(52,152,219,0.2)'
            }
        },
        label: {
            display: 'block',
            marginBottom: '8px',
            fontWeight: '500',
            color: '#333',
            fontSize: '14px'
        },
        vitalInput: {
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '15px',
            flex: '1',
            minWidth: '200px'
        },
        classificationMessage: (level) => ({
            color: level === 'low' || level === 'high' ? '#e74c3c' : '#27ae60',
            fontWeight: '500',
            fontSize: '13px',
            marginTop: '5px',
            fontStyle: 'italic'
        }),
        modalOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
        },
        modalContent: {
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
        },
        modalHeader: {
            color: '#333',
            marginBottom: '25px',
            fontSize: '22px',
            fontWeight: '500',
            borderBottom: '1px solid #eee',
            paddingBottom: '10px'
        },
        patientGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '15px',
            marginBottom: '25px'
        },
        patientField: {
            marginBottom: '15px'
        },
        patientLabel: {
            display: 'block',
            marginBottom: '5px',
            fontWeight: '500',
            color: '#555',
            fontSize: '13px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        patientInput: {
            width: '100%',
            padding: '10px',
            border: '1px solid #eee',
            borderRadius: '4px',
            backgroundColor: '#fafafa',
            fontSize: '15px',
            color: '#333'
        },
        radioGroup: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginTop: '10px'
        },
        radioOption: {
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer'
        },
        radioInput: {
            marginRight: '10px',
            cursor: 'pointer'
        },
        radioLabel: {
            fontSize: '15px',
            color: '#333',
            cursor: 'pointer'
        },
        testItem: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0',
            borderBottom: '1px solid #eee'
        },
        testName: {
            flex: 1,
            fontSize: '15px',
            color: '#333'
        },
        testPrice: {
            marginRight: '15px',
            color: '#7f8c8d',
            fontSize: '14px'
        },
        modalFooter: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '15px',
            marginTop: '25px',
            paddingTop: '20px',
            borderTop: '1px solid #eee'
        },
        successModal: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000
        },
        successContent: {
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '8px',
            textAlign: 'center',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 5px 20px rgba(0,0,0,0.2)'
        },
        successIcon: {
            fontSize: '50px',
            color: '#27ae60',
            marginBottom: '20px'
        },
        successMessage: {
            fontSize: '18px',
            color: '#333',
            marginBottom: '25px',
            lineHeight: '1.5'
        },
        testList: {
            maxHeight: "300px",
            overflowY: "auto",
            border: "1px solid #eee",
            padding: "10px",
            borderRadius: "4px",
            margin: "10px 0"
        },
        testItem: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 0",
            borderBottom: "1px solid #f1f1f1"
        },
        removeButton: {
            backgroundColor: "#e74c3c",
            color: "white",
            border: "none",
            borderRadius: "4px",
            padding: "5px 10px",
            cursor: "pointer",
            fontSize: "0.8rem"
        },
        selectedTestsContainer: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
            marginTop: "20px"
        },
        selectedTestsBox: {
            backgroundColor: "#f8f9fa",
            padding: "15px",
            borderRadius: "6px",
            border: "1px solid #ddd"
        },
        selectedTestsTitle: {
            fontSize: "1rem",
            marginBottom: "10px",
            color: "#3498db"
        },
        selectedTestsList: {
            listStyle: "none",
            padding: 0,
            margin: 0
        },
        selectedTestItem: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 0",
            borderBottom: "1px solid #eee"
        }
    };


    // [Rest of the component logic remains the same...]

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
            
    
            <div style={{ paddingTop: '35px', ...styles.container }}>
            <ToastContainer position="top-right" autoClose={5000} />
            <Topbar token={urlToken} />
              <h1 style={{ ...styles.header, marginTop: 40 }}>PATIENT TRIAGE</h1>
    
                {/* Patient Details Section */}
                {readOnlyPatientDetails && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionHeading}>Patient Information</h2>
                        <div style={styles.patientGrid}>
                            {Object.entries(readOnlyPatientDetails).map(([key, value]) => (
                                <div key={key} style={styles.patientField}>
                                    <label style={styles.patientLabel}>
                                        {key.replace(/([A-Z])/g, ' $1').trim()}:
                                    </label>
                                    <input
                                        type="text"
                                        value={value || ''}
                                        readOnly
                                        style={styles.patientInput}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Add Patient Button */}
                <div style={styles.section}>
                    <button 
                        onClick={() => setShowAddPatientPrompt(true)}
                        style={{ ...styles.button, ...styles.primaryButton }}
                    >
                        {readOnlyPatientDetails ? 'Change Patient' : 'Add Patient Details'}
                    </button>
                </div>

                {/* Patient Vitals Section */}
                <div style={styles.section}>
                    <h2 style={styles.sectionHeading}>Patient Vitals</h2>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                        {/* Blood Pressure */}
                        <div style={styles.vitalInput}>
                            <label style={styles.label}>Blood Pressure (mmHg)</label>
                            <input
                                type="text"
                                placeholder="120/80"
                                value={bloodPressure}
                                onChange={(e) => setBloodPressure(e.target.value)}
                                style={styles.input}
                            />
                            {bloodPressureStatus.status && (
                                <span style={styles.classificationMessage(bloodPressureStatus.level)}>
                                    {bloodPressureStatus.status}
                                </span>
                            )}
                        </div>

                        {/* Pulse Rate */}
                        <div style={styles.vitalInput}>
                            <label style={styles.label}>Pulse Rate (bpm)</label>
                            <input
                                type="text"
                                placeholder="72"
                                value={pulseRate}
                                onChange={(e) => setPulseRate(e.target.value)}
                                style={styles.input}
                            />
                            {pulseRateStatus.status && (
                                <span style={styles.classificationMessage(pulseRateStatus.level)}>
                                    {pulseRateStatus.status}
                                </span>
                            )}
                        </div>

                        {/* Temperature */}
                        <div style={styles.vitalInput}>
                            <label style={styles.label}>Temperature (°C)</label>
                            <input
                                type="text"
                                placeholder="36.5"
                                value={temperature}
                                onChange={(e) => setTemperature(e.target.value)}
                                style={styles.input}
                            />
                            {temperatureStatus && (
                                <span style={styles.classificationMessage(
                                    temperatureStatus.includes('Very High') ? 'high' : 
                                    temperatureStatus.includes('High') ? 'high' : 
                                    temperatureStatus.includes('Low') ? 'low' : 'normal'
                                )}>
                                    {temperatureStatus}
                                </span>
                            )}
                        </div>

                        {/* SPO2 */}
                        <div style={styles.vitalInput}>
                            <label style={styles.label}>SPO2 (%)</label>
                            <input
                                type="text"
                                placeholder="98"
                                value={spo2}
                                onChange={(e) => setSpo2(e.target.value)}
                                style={styles.input}
                            />
                            {spo2Status && (
                                <span style={styles.classificationMessage(spo2Status.includes('Warning') ? 'low' : 'normal')}>
                                    {spo2Status}
                                </span>
                            )}
                        </div>

                        {/* Respiratory Rate */}
                        <div style={styles.vitalInput}>
                            <label style={styles.label}>Respiratory Rate</label>
                            <input
                                type="text"
                                placeholder="16"
                                value={respiratoryRate}
                                onChange={(e) => setRespiratoryRate(e.target.value)}
                                style={styles.input}
                            />
                            {respiratoryRateStatus && (
                                <span style={styles.classificationMessage(respiratoryRateStatus.includes('Warning') ? 'high' : 'normal')}>
                                    {respiratoryRateStatus}
                                </span>
                            )}
                        </div>

                        {/* Body Weight */}
                        <div style={styles.vitalInput}>
                            <label style={styles.label}>Body Weight (kg)</label>
                            <input
                                type="text"
                                placeholder="70"
                                value={bodyWeight}
                                onChange={(e) => setBodyWeight(e.target.value)}
                                style={styles.input}
                            />
                        </div>

                        {/* Height */}
                        <div style={styles.vitalInput}>
                            <label style={styles.label}>Height (cm)</label>
                            <input
                                type="text"
                                placeholder="170"
                                value={height}
                                onChange={(e) => setHeight(e.target.value)}
                                style={styles.input}
                            />
                        </div>

                        {/* BMI */}
                        <div style={styles.vitalInput}>
                            <label style={styles.label}>BMI</label>
                            <div style={{
                                padding: '12px',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '4px',
                                minHeight: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '15px',
                                color: '#333'
                            }}>
                                {bmi || '--'}
                            </div>
                            {bmiWarning && (
                                <span style={styles.classificationMessage(
                                    bmiWarning.includes('Underweight') || 
                                    bmiWarning.includes('Overweight') || 
                                    bmiWarning.includes('Obese') ? 'high' : 'normal'
                                )}>
                                    {bmiWarning}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Visit Classification */}
                <div style={styles.section}>
                    <h2 style={styles.sectionHeading}>Visit Classification</h2>
                    <div style={styles.radioGroup}>
                        {['referral', 'scheduled', 'emergency', 'follow-up', 'routine', 'consultation', 'Walk-in for Specific Services'].map((type) => (
                            <label key={type} style={styles.radioOption}>
                                <input
                                    type="radio"
                                    id={type}
                                    name="visitClassification"
                                    checked={patientNature === type}
                                    onChange={() => setPatientNature(type)}
                                    style={styles.radioInput}
                                />
                                <span style={styles.radioLabel}>
                                    {type.replace('-', ' ').charAt(0).toUpperCase() + type.replace('-', ' ').slice(1)}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Signs and Symptoms */}
                <div style={styles.section}>
                    <label style={styles.label}>Any message to the Doctor</label>
                    <textarea
                        placeholder="Enter signs and symptoms, doctor preference, or any relevant information..."
                        value={signsAndSymptoms}
                        onChange={(e) => setSignsAndSymptoms(e.target.value)}
                        style={{
                            ...styles.input,
                            minHeight: '100px',
                            resize: 'vertical'
                        }}
                    />
                </div>

                <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Patient Routing</h2>
                
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                    <div>
                        <div style={{ marginBottom: "15px" }}>
                            <input
                                type="checkbox"
                                id="doctor"
                                name="action"
                                value="doctor"
                                checked={selectionState.doctor}
                                onChange={() => handleOptionToggle('doctor')}
                                style={{ marginRight: "10px" }}
                            />
                            <label htmlFor="doctor" style={styles.label}>
                                Send this patient to the doctor
                            </label>
                        </div>

                        <div style={{ marginBottom: "15px" }}>
                            <input
                                type="checkbox"
                                id="familyPlanning"
                                name="action"
                                value="familyPlanning"
                                checked={selectionState.familyPlanning}
                                onChange={() => handleOptionToggle('familyPlanning')}
                                style={{ marginRight: "10px" }}
                            />
                            <label htmlFor="familyPlanning" style={styles.label}>
                                Send to family planning department
                            </label>
                        </div>
                    </div>

                    <div>
                        <div style={{ marginBottom: "15px" }}>
                            <input
                                type="checkbox"
                                id="lab"
                                name="action"
                                value="lab"
                                checked={selectionState.lab}
                                onChange={() => handleOptionToggle('lab')}
                                style={{ marginRight: "10px" }}
                            />
                            <label htmlFor="lab" style={styles.label}>
                                Patient only wants lab tests
                            </label>
                        </div>

                        <div style={{ marginBottom: "15px" }}>
                            <input
                                type="checkbox"
                                id="radiology"
                                name="action"
                                value="radiology"
                                checked={selectionState.radiology}
                                onChange={() => handleOptionToggle('radiology')}
                                style={{ marginRight: "10px" }}
                            />
                            <label htmlFor="radiology" style={styles.label}>
                                Patient only needs radiology
                            </label>
                        </div>
                    </div>
                </div>

                {/* Selected Tests Display */}
                {(selectedLabTests.length > 0 || selectedRadiologyTests.length > 0) && (
                    <div style={styles.selectedTestsContainer}>
                        {selectedLabTests.length > 0 && (
                            <div style={styles.selectedTestsBox}>
                                <h3 style={styles.selectedTestsTitle}>Selected Lab Tests</h3>
                                <ul style={styles.selectedTestsList}>
                                    {selectedLabTests.map((test, index) => (
                                        <li key={index} style={styles.selectedTestItem}>
                                            <span>{test}</span>
                                            <button
                                                onClick={() => {
                                                    setSelectedLabTests(prevTests => prevTests.filter(prevTest => prevTest !== test));
                                                }}
                                                style={styles.removeButton}
                                            >
                                                Remove
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {selectedRadiologyTests.length > 0 && (
                            <div style={styles.selectedTestsBox}>
                                <h3 style={styles.selectedTestsTitle}>Selected Radiology Exams</h3>
                                <ul style={styles.selectedTestsList}>
                                    {selectedRadiologyTests.map((exam, index) => (
                                        <li key={index} style={styles.selectedTestItem}>
                                            <span>{exam}</span>
                                            <button
                                                onClick={() => {
                                                    setSelectedRadiologyTests(prevTests => prevTests.filter(prevTest => prevTest !== exam));
                                                }}
                                                style={styles.removeButton}
                                            >
                                                Remove
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>


                {/* Attention Level */}
                <div style={styles.section}>
                    <h2 style={styles.sectionHeading}>Priority Level</h2>
                    <div style={styles.radioGroup}>
                        {['critical', 'average', 'routine'].map((level) => (
                            <label key={level} style={styles.radioOption}>
                                <input
                                    type="radio"
                                    id={level}
                                    name="attention"
                                    checked={attentionLevel === level}
                                    onChange={() => toggleAttentionLevel(level)}
                                    style={styles.radioInput}
                                />
                                <span style={styles.radioLabel}>
                                    {level === 'critical'
                                        ? 'Critical - Immediate Attention Required'
                                        : level === 'average'
                                        ? 'Moderate - Prompt Attention Needed'
                                        : 'Routine - Standard Priority'}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
        
                {/* Submit Button */}
                <div style={{ textAlign: 'center', marginTop: '40px' }}>
                    <button
                        onClick={submitForm}
                        disabled={submitting || !readOnlyPatientDetails}
                        style={{
                            ...styles.button,
                            ...styles.primaryButton,
                            padding: '15px 30px',
                            fontSize: '16px',
                            backgroundColor: submitting || !readOnlyPatientDetails ? '#95a5a6' : '#3498db',
                            opacity: submitting || !readOnlyPatientDetails ? 0.7 : 1,
                            cursor: submitting || !readOnlyPatientDetails ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {submitting ? 'Processing...' : 'Complete Triage'}
                    </button>
                </div>
            </div>

            {/* [Modal components remain the same but with updated styles...] */}
            {/* Add Patient Modal */}
            {showAddPatientPrompt && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <h2 style={styles.modalHeader}>Patient Registration</h2>
                        
                        {/* First Name with Suggestions */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={styles.label}>First Name</label>
                            <input
                                type="text"
                                placeholder="First Name"
                                value={newPatientDetails.firstName}
                                onChange={(e) => {
                                    setNewPatientDetails({ ...newPatientDetails, firstName: e.target.value });
                                    handleSuggestName(e.target.value);
                                }}
                                style={styles.input}
                            />
                            {suggestions.length > 0 && (
                                <div style={{
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    border: '1px solid #eee',
                                    borderRadius: '4px',
                                    marginTop: '5px'
                                }}>
                                    {suggestions.map((patient, index) => (
                                        <div
                                            key={index}
                                            onClick={() => handleSelectSuggestedPatient(patient)}
                                            style={{
                                                padding: '12px',
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #f5f5f5',
                                                ':hover': {
                                                    backgroundColor: '#f9f9f9'
                                                }
                                            }}
                                        >
                                            {patient.first_name} {patient.last_name} (ID: {patient.contact_id})
                                        </div>
                                    ))}
                                </div>
                            )}
                            {message && <p style={{ color: '#7f8c8d', fontSize: '13px', marginTop: '5px' }}>{message}</p>}
                        </div>

                        {/* Last Name */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={styles.label}>Last Name</label>
                            <input
                                type="text"
                                placeholder="Last Name"
                                value={newPatientDetails.lastName}
                                onChange={(e) => setNewPatientDetails({ ...newPatientDetails, lastName: e.target.value })}
                                style={styles.input}
                            />
                        </div>

                        {/* Phone Number */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={styles.label}>Phone Number</label>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <FontAwesomeIcon icon={faPhone} style={{ marginRight: '10px', color: '#7f8c8d' }} />
                                <input
                                    type="tel"
                                    placeholder="Phone Number"
                                    value={newPatientDetails.phoneNumber}
                                    onChange={(e) => setNewPatientDetails({ ...newPatientDetails, phoneNumber: e.target.value })}
                                    style={{ ...styles.input, flex: 1 }}
                                />
                            </div>
                        </div>

                        {/* Age Section */}
                        <div style={{ 
                            marginBottom: '20px',
                            padding: '20px',
                            border: '1px solid #eee',
                            borderRadius: '8px',
                            backgroundColor: '#fafafa'
                        }}>
                            <label style={{ ...styles.label, marginBottom: '15px' }}>Age</label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                                <div>
                                    <label style={{ ...styles.label, fontSize: '13px' }}>Years</label>
                                    <input
                                        type="number"
                                        placeholder="Years"
                                        value={newPatientDetails.years}
                                        onChange={(e) => setNewPatientDetails({ ...newPatientDetails, years: e.target.value })}
                                        style={styles.input}
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label style={{ ...styles.label, fontSize: '13px' }}>Months</label>
                                    <input
                                        type="number"
                                        placeholder="Months"
                                        value={newPatientDetails.months}
                                        onChange={(e) => setNewPatientDetails({ ...newPatientDetails, months: e.target.value })}
                                        style={styles.input}
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label style={{ ...styles.label, fontSize: '13px' }}>Weeks</label>
                                    <input
                                        type="number"
                                        placeholder="Weeks"
                                        value={newPatientDetails.weeks}
                                        onChange={(e) => setNewPatientDetails({ ...newPatientDetails, weeks: e.target.value })}
                                        style={styles.input}
                                        min="0"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Sex */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={styles.label}>Gender</label>
                            <select
                                value={newPatientDetails.sex}
                                onChange={(e) => setNewPatientDetails({ ...newPatientDetails, sex: e.target.value })}
                                style={styles.input}
                            >
                                <option value="">Select Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                            </select>
                        </div>

                        {/* Religion */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={styles.label}>Religion</label>
                            <select
                                value={newPatientDetails.religion}
                                onChange={(e) => setNewPatientDetails({ ...newPatientDetails, religion: e.target.value })}
                                style={styles.input}
                            >
                                <option value="">Select Religion</option>
                                <option value="Christian">Christian</option>
                                <option value="Islamic">Islamic</option>
                            </select>
                        </div>

                        {/* Address */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={styles.label}>Address</label>
                            <input
                                type="text"
                                placeholder="Full Address"
                                value={newPatientDetails.address}
                                onChange={(e) => setNewPatientDetails({ ...newPatientDetails, address: e.target.value })}
                                style={styles.input}
                            />
                        </div>

                        {/* Date of Birth */}
                        <div style={{ marginBottom: '25px' }}>
                            <label style={styles.label}>Date of Birth (month/date/year):</label>
                          
    <input
      type="date"
      value={formatDate(newPatientDetails.dob)}
      onChange={(e) => setNewPatientDetails({ ...newPatientDetails, dob: e.target.value })}
      className="add-patient-input"
    />
                        </div>

                        {/* Modal Buttons */}
                        <div style={styles.modalFooter}>
                            <button
                                onClick={handleCancel}
                                style={{
                                    ...styles.button,
                                    backgroundColor: '#95a5a6'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={addContact}
                                disabled={savePatientClicked}
                                style={{
                                    ...styles.button,
                                    ...styles.successButton,
                                    backgroundColor: savePatientClicked ? '#95a5a6' : '#27ae60'
                                }}
                            >
                                {savePatientClicked ? 'Saving...' : 'Save Patient'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* [Other modals (Lab Tests, Radiology Tests, Success) follow similar style updates...] */}
            {/* Success Modal */}
            {/* Lab Tests Modal */}
{showLabTestPrompt && (
    <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
    }} onClick={(e) => {
        if (e.target.style.position === "fixed") {
            setShowLabTestPrompt(false);
            if (selectedLabTests.length === 0) {
                setSelectionState(prevState => ({ ...prevState, lab: false }));
            }
        }
    }}>
        <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 5px 20px rgba(0,0,0,0.2)'
        }}>
            <div style={{
                padding: '20px',
                borderBottom: '1px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <h2 style={{
                    margin: 0,
                    fontSize: '1.5rem',
                    color: '#2c3e50'
                }}>Select Laboratory Tests</h2>
                <button 
                    onClick={() => {
                        setShowLabTestPrompt(false);
                        if (selectedLabTests.length === 0) {
                            setSelectionState(prevState => ({ ...prevState, lab: false }));
                        }
                    }}
                    style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        color: '#7f8c8d'
                    }}
                >
                    ×
                </button>
            </div>
            
           
            <div style={{
                flex: 1,
                overflowY: 'auto',
                maxHeight: '50vh'
            }}>
                {labTestsAvailable.map(test => (
                    <div key={test.name} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 15px',
                        borderBottom: '1px solid #eee',
                        backgroundColor: selectedLabTests.includes(test.name) ? '#e8f4fd' : 'white',
                        transition: 'background-color 0.2s'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            flex: 1
                        }}>
                            <input
                                type="checkbox"
                                id={`lab-${test.name}`}
                                value={test.name}
                                checked={selectedLabTests.includes(test.name)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setSelectedLabTests(prevTests => [...prevTests, test.name]);
                                    } else {
                                        setSelectedLabTests(prevTests => prevTests.filter(prevTest => prevTest !== test.name));
                                    }
                                }}
                                style={{
                                    marginRight: '12px',
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer'
                                }}
                            />
                            <label 
                                htmlFor={`lab-${test.name}`}
                                style={{
                                    cursor: 'pointer',
                                    flex: 1,
                                    marginRight: '10px'
                                }}
                            >
                                {test.name}
                            </label>
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <span style={{
                                fontWeight: '500',
                                color: '#2c3e50',
                                minWidth: '80px',
                                textAlign: 'right'
                            }}>
                                {test.price.toLocaleString()}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
            
            <div style={{
                padding: '15px',
                borderTop: '1px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#f8f9fa'
            }}>
                <div>
                    <strong>Total Selected:</strong> {selectedLabTests.length} tests
                </div>
                <div style={{
                    display: 'flex',
                    gap: '10px'
                }}>
                    <button 
                        onClick={() => {
                            setSelectedLabTests([]);
                        }}
                        style={{
                            padding: '8px 15px',
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                        }}
                    >
                        Clear All
                    </button>
                    <button 
                        onClick={() => setShowLabTestPrompt(false)}
                        style={{
                            padding: '8px 20px',
                            backgroundColor: '#3498db',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                        }}
                    >
                        Confirm Selection
                    </button>
                </div>
            </div>
        </div>
    </div>
)}

{/* Radiology Tests Modal */}
{showRadiologyTestPrompt && (
    <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
    }} onClick={(e) => {
        if (e.target.style.position === "fixed") {
            setShowRadiologyTestPrompt(false);
            if (selectedRadiologyTests.length === 0) {
                setSelectionState(prevState => ({ ...prevState, radiology: false }));
            }
        }
    }}>
        <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 5px 20px rgba(0,0,0,0.2)'
        }}>
            <div style={{
                padding: '20px',
                borderBottom: '1px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <h2 style={{
                    margin: 0,
                    fontSize: '1.5rem',
                    color: '#2c3e50'
                }}>Select Radiology Examinations</h2>
                <button 
                    onClick={() => {
                        setShowRadiologyTestPrompt(false);
                        if (selectedRadiologyTests.length === 0) {
                            setSelectionState(prevState => ({ ...prevState, radiology: false }));
                        }
                    }}
                    style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        color: '#7f8c8d'
                    }}
                >
                    ×
                </button>
            </div>
            
           
            <div style={{
                flex: 1,
                overflowY: 'auto',
                maxHeight: '50vh'
            }}>
                {radiologyTestsAvailable.map(test => (
                    <div key={test.name} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 15px',
                        borderBottom: '1px solid #eee',
                        backgroundColor: selectedRadiologyTests.includes(test.name) ? '#e8f4fd' : 'white',
                        transition: 'background-color 0.2s'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            flex: 1
                        }}>
                            <input
                                type="checkbox"
                                id={`rad-${test.name}`}
                                value={test.name}
                                checked={selectedRadiologyTests.includes(test.name)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setSelectedRadiologyTests(prevTests => [...prevTests, test.name]);
                                    } else {
                                        setSelectedRadiologyTests(prevTests => prevTests.filter(prevTest => prevTest !== test.name));
                                    }
                                }}
                                style={{
                                    marginRight: '12px',
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer'
                                }}
                            />
                            <label 
                                htmlFor={`rad-${test.name}`}
                                style={{
                                    cursor: 'pointer',
                                    flex: 1,
                                    marginRight: '10px'
                                }}
                            >
                                {test.name}
                            </label>
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <span style={{
                                fontWeight: '500',
                                color: '#2c3e50',
                                minWidth: '80px',
                                textAlign: 'right'
                            }}>
                                {test.price.toLocaleString()}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
            
            <div style={{
                padding: '15px',
                borderTop: '1px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#f8f9fa'
            }}>
                <div>
                    <strong>Total Selected:</strong> {selectedRadiologyTests.length} exams
                </div>
                <div style={{
                    display: 'flex',
                    gap: '10px'
                }}>
                    <button 
                        onClick={() => {
                            setSelectedRadiologyTests([]);
                        }}
                        style={{
                            padding: '8px 15px',
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                        }}
                    >
                        Clear All
                    </button>
                    <button 
                        onClick={() => setShowRadiologyTestPrompt(false)}
                        style={{
                            padding: '8px 20px',
                            backgroundColor: '#3498db',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                        }}
                    >
                        Confirm Selection
                    </button>
                </div>
            </div>
        </div>
    </div>
)}
            {successModal && (
                <div style={styles.successModal}>
                    <div style={styles.successContent}>
                        <div style={styles.successIcon}>✓</div>
                        <h2 style={{ color: '#27ae60', marginBottom: '15px' }}>Triage Complete</h2>
                        <p style={styles.successMessage}>{successMessage}</p>
                        <button
                            onClick={() => {
                                setSuccessModal(false);
                                setSuccessMessage('');
                                setPatientAction('');
                            }}
                            style={{
                                ...styles.button,
                                ...styles.successButton,
                                padding: '12px 30px'
                            }}
                        >
                            Continue
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Triage;